import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

/**
 * The reference's own popup placement, as one hook.
 *
 * `Niraxo_bid_masters.html` does not hand its popups to a positioning library.
 * It measures the trigger with `getBoundingClientRect()` and draws the popup
 * `position: fixed` from that rect, which is what keeps a row's action menu out
 * of the table's horizontal scroller: an absolutely-positioned popup inherits
 * whatever clipping and scroll offset its container has, a fixed one answers to
 * the viewport alone.
 *
 * Two placements, both copied from the reference:
 *
 *   row action menu   left = max(EDGE, rect.right - width)
 *                     top  = min(rect.bottom + GAP, innerHeight - height - EDGE)
 *
 *   anything else     left = rect.left, flipped above the trigger when
 *                     `rect.bottom + GAP + height` would run past the viewport
 *
 * The first clamps, the second flips; the reference uses clamping where the
 * popup is short and predictable and flipping where its height follows its
 * contents. Both keep a 12px margin from every viewport edge.
 */

/** Gap the reference leaves between a trigger and its popup. */
const GAP = 6;

/** Margin the reference keeps between a popup and the viewport edge. */
const EDGE = 12;

/** The trigger's box, in viewport coordinates -- what `fixed` is measured in. */
interface AnchorRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
}

export interface AnchoredPopupOptions {
  /** Popup width in px. The reference fixes this per popup rather than measuring. */
  width: number;
  /**
   * Popup height in px.
   *
   * Stated rather than measured because the placement has to be known for the
   * first paint -- measuring would put the popup on screen in the wrong place
   * and move it a frame later.
   */
  height: number;
  /**
   * `end` right-aligns the popup to the trigger and clamps it into the
   * viewport, which is the row action menu; `start` left-aligns and flips.
   */
  align: 'start' | 'end';
  /** Stacking level. The reference gives each kind of popup an explicit one. */
  zIndex: number;
}

export interface AnchoredPopupResult {
  open: boolean;
  /** Put on the popup element; it carries the whole fixed placement. */
  style: CSSProperties;
  /** Put on the popup element, so an outside click can exclude it. */
  popupRef: React.RefObject<HTMLDivElement | null>;
  /** The trigger's click handler: measures, then opens or closes. */
  toggle: (event: ReactMouseEvent<HTMLElement>) => void;
  close: () => void;
}

function placement(
  rect: AnchorRect | null,
  { width, height, align, zIndex }: AnchoredPopupOptions,
): CSSProperties {
  const base: CSSProperties = { position: 'fixed', width, zIndex };

  /*
    No rect means the trigger was never measured -- the reference centres the
    popup rather than dropping it at 0,0 in the corner.
  */
  if (!rect) {
    return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  if (align === 'end') {
    return {
      ...base,
      left: Math.max(EDGE, rect.right - width),
      top: Math.min(rect.bottom + GAP, Math.max(EDGE, viewportHeight - height - EDGE)),
    };
  }

  const flipUp = rect.bottom + GAP + height > viewportHeight - EDGE;

  return {
    ...base,
    left: Math.min(Math.max(EDGE, rect.left), Math.max(EDGE, viewportWidth - width - EDGE)),
    top: flipUp ? Math.max(EDGE, rect.top - GAP - height) : rect.bottom + GAP,
  };
}

/**
 * An anchored popup that dismisses itself.
 *
 * Closes on an outside pointer press, on Escape, and on a scroll or resize --
 * the last two because a popup placed from a rect measured at open time is
 * stale the moment its trigger moves, and a stale popup pointing at the wrong
 * row is worse than one that closed. This is also what PrimeReact's own
 * overlays do, so nothing that used to dismiss stops dismissing.
 */
export function useAnchoredPopup(options: AnchoredPopupOptions): AnchoredPopupResult {
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const [open, setOpen] = useState(false);

  const popupRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    const trigger = event.currentTarget;
    const box = trigger.getBoundingClientRect();

    triggerRef.current = trigger;
    setRect({
      left: box.left,
      right: box.right,
      top: box.top,
      bottom: box.bottom,
      width: box.width,
    });
    setOpen((previous) => !previous);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (target === null) return;

      /* A press on the trigger is the toggle's business, not a dismissal. */
      if (popupRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;

      setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    const onMove = () => {
      setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    /* Captured: a scroll inside the table does not bubble to the document. */
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  return { open, style: placement(rect, options), popupRef, toggle, close };
}
